import {
  API_CALL,
  CREATE_SEARCH,
  CREATE_SEARCH_SESSION,
  FETCH_RECENT_SEARCHES,
  SET_RECENT_SEARCHES,
  INVALIDATE_USER_RECENT_SEARCHES,
  TYPEAHEAD_STREAMING_START,
  TYPEAHEAD_STREAMING_COMPLETE,
  TYPEAHEAD_STREAMING_ERROR,
  SET_TYPEAHEAD_STREAMING_SOCKET_STATUS,
  SET_TYPEAHEAD_USER_SELECTED_DATES,
  SET_TYPEAHEAD_USER_DATES_INTERACTED,
  SET_TYPEAHEAD_USER_SELECTED_OCCUPANCY,
} from './actionConstants'
import { FETCH_RECOMMENDATION_SUGGESTIONS, FETCH_USER_RECENT_SEARCHES } from './apiActionConstants'
import uuidV4 from 'lib/string/uuidV4Utils'
import type { AppAction } from 'actions/types/ActionTypes'
import { deleteRecentSearch, getRecentSearches, hydrateRecentSearchesFromUserRecentSearches, saveRecentSearch } from 'components/Search/utils'
import { setFlightSessionRecentSearch } from 'lib/flights/flightUtils'
import {
  addSearchSocketListener,
  removeAllSearchSocketListenersAndSafelyDisconnect,
  addStateListener,
} from 'lib/search/searchSocketListeners'
import { emitSocketMessage, clearLastMessage } from 'lib/search/socketEmitter'
import {
  getOrCreateTypeaheadStreamBatcher,
  removeTypeaheadStreamBatcher,
} from 'lib/search/typeaheadStreamBatcher'
import { TYPEAHEAD_FIELD_SOCKET_EVENTS } from 'lib/search/typeaheadStreamFieldEvents'
import config from 'constants/config'
import { HOTEL_SEARCH_TYPEAHEAD_TYPES } from 'constants/search'
import { getRecommendationSuggestions, getUserRecentSearches } from 'api/search'
import { getCurrentUserId } from 'selectors/accountSelectors'
import { getDisplayPlaceId, mapTypeaheadEndEventToSearchItemV2, mapTypeaheadResultToSearchItemV2 } from 'api/mappers/Search/typeaheadStreamingMap'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getBrowserTimeZone } from 'lib/datetime/dateUtils'
import type {
  TypeaheadEndResponse,
  TypeaheadErrorResponse,
  TypeaheadExtractRequest,
  TypeaheadNaturalLanguageResultsV2Response,
} from '@luxuryescapes/contract-search'
import type { TypeaheadSocketResponseBase } from 'lib/search/typeaheadStreamFieldEvents'

/** Reuse a same-account recent-search response only for a short homepage session. */
const USER_RECENT_SEARCHES_CACHE_TTL_MS = 5 * 60 * 1000

export function createSearch(
  action: { searchKey: string, searchId: string, searchContext?: App.SearchContext, searchEvent?: App.SearchEvent },
): AppAction {
  const { searchKey, searchId, searchContext, searchEvent } = action

  return {
    type: CREATE_SEARCH,
    key: searchKey,
    searchContext,
    searchEvent,
    searchId,
  }
}

export function createSearchSession(
  action: { searchKey: string, searchSessionId: string },
): AppAction {
  const { searchKey, searchSessionId } = action

  return {
    type: CREATE_SEARCH_SESSION,
    key: searchKey,
    searchSessionId,
  }
}

export function setSearchEvent(
  searchKey: string,
  options: { newSearchId?: string, searchContext?: App.SearchContext, searchEvent?: App.SearchEvent } = {},
): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const { newSearchId, searchContext, searchEvent } = options

    const searchId = state.search.searches[searchKey]?.searchId ?? newSearchId ?? uuidV4()

    dispatch(createSearch({ searchKey, searchId, searchContext, searchEvent }))
  }
}

export function setSearchSession(
  searchSessionKey: string,
  options: { searchSessionId: string },
): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const { searchSessionId: newSearchSessionId } = options

    const searchSessionId = state.search.searchSessions[searchSessionKey]?.searchSessionId ?? newSearchSessionId ?? uuidV4()

    dispatch(createSearchSession({ searchKey: searchSessionKey, searchSessionId }))
  }
}

export function fetchRecentSearches(): AppAction {
  return async(dispatch, getState) => {
    const state = getState()
    const memberId = state.auth.account.memberId
    const domainUserId = state.auth.domainUserId
    const userKey = memberId ?? domainUserId

    dispatch({
      type: FETCH_RECENT_SEARCHES,
      data: getRecentSearches(userKey),
      userKey,
    })

    if (!userKey) {
      return
    }

    try {
      const userRecentSearches = await getUserRecentSearches(memberId ? { leUserId: memberId } : { domainUserId: userKey })
      const currentState = getState()
      const currentUserKey = currentState.auth.account.memberId ?? currentState.auth.domainUserId

      if (currentUserKey !== userKey) return

      dispatch({
        type: FETCH_RECENT_SEARCHES,
        data: hydrateRecentSearchesFromUserRecentSearches(userRecentSearches, userKey),
        userKey,
      })
    } catch {
      // Keep the localStorage-backed state in place if the API refresh fails.
    }
  }
}

export function fetchRecommendationSuggestions(placeIds: Array<string>): AppAction {
  return async(dispatch, getState) => {
    const state = getState()
    const key = placeIds.join(',')

    if (state.search.recommendationSuggestions[key]) return

    dispatch({
      type: API_CALL,
      api: FETCH_RECOMMENDATION_SUGGESTIONS,
      request: () => getRecommendationSuggestions(state.geo.currentRegionCode, placeIds),
      key,
    })
  }
}

export function fetchUserRecentSearches(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const memberId = getCurrentUserId(state)
    const domainUserId = state.auth.domainUserId
    const userKey = memberId ?? domainUserId
    if (!userKey) return

    const current = state.search.userRecentSearches
    const requestedAtMs = current.requestedAt ? Date.parse(current.requestedAt) : Number.NaN
    const isFresh = current.state === 'done'
      && current.cacheKey === userKey
      && Number.isFinite(requestedAtMs)
      && Date.now() - requestedAtMs < USER_RECENT_SEARCHES_CACHE_TTL_MS
    if (current.state === 'loading' && current.cacheKey === userKey) return
    if (isFresh) return

    dispatch({
      type: API_CALL,
      api: FETCH_USER_RECENT_SEARCHES,
      cacheKey: userKey,
      requestGeneration: (current.requestGeneration ?? 0) + 1,
      request: () => getUserRecentSearches(memberId ? { leUserId: memberId } : { domainUserId: userKey }),
    })
  }
}

export function addRecentSearch(
  searchItem: App.SearchItem,
  dates: App.SearchDates,
  rooms: Array<App.Occupants>,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const userKey = state.auth.account.memberId ?? state.auth.domainUserId

    saveRecentSearch(searchItem, dates, rooms, userKey)

    dispatch({
      type: SET_RECENT_SEARCHES,
      data: getRecentSearches(userKey),
      userKey,
    })
    // The carousel reads the account history endpoint, not this local list.
    // Drop freshness so the next homepage visit refetches, while an untouched cache keeps its TTL.
    dispatch({ type: INVALIDATE_USER_RECENT_SEARCHES })
  }
}

export function removeRecentSearch(
  id: string,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const userKey = state.auth.account.memberId ?? state.auth.domainUserId

    deleteRecentSearch(id, userKey)

    dispatch({
      type: SET_RECENT_SEARCHES,
      data: getRecentSearches(userKey),
      userKey,
    })
  }
}

export function clearRecentSearches(): AppAction {
  return (dispatch) => {
    dispatch({
      type: SET_RECENT_SEARCHES,
      data: [],
      userKey: undefined,
    })
  }
}

export function clearAllSearchState(): AppAction {
  return (dispatch) => {
    // Clear Redux state for recent searches
    dispatch(clearRecentSearches())

    // Clear localStorage flight session data
    setFlightSessionRecentSearch({
      flights: [],
    })
  }
}

// Typeahead Streaming Actions

export function startTypeaheadStreaming(
  typeaheadId: string,
): AppAction {
  return {
    type: TYPEAHEAD_STREAMING_START,
    typeaheadId,
  }
}

export function completeTypeaheadStreaming(
  action: { typeaheadId: string; input: string },
): AppAction {
  const { typeaheadId, input } = action

  return {
    type: TYPEAHEAD_STREAMING_COMPLETE,
    typeaheadId,
    input,
  }
}

export function errorTypeaheadStreaming(typeaheadId: string): AppAction {
  return {
    type: TYPEAHEAD_STREAMING_ERROR,
    typeaheadId,
  }
}

export function setTypeaheadUserSelectedDates(
  action: {
    typeaheadId: string
    dates: App.TypeaheadExtractedTravelDates | undefined
  },
): AppAction {
  const { typeaheadId, dates } = action

  return {
    type: SET_TYPEAHEAD_USER_SELECTED_DATES,
    typeaheadId,
    dates,
  }
}

export function setTypeaheadUserDatesInteracted(
  action: {
    typeaheadId: string
  },
): AppAction {
  const { typeaheadId } = action

  return {
    type: SET_TYPEAHEAD_USER_DATES_INTERACTED,
    typeaheadId,
  }
}

export function setTypeaheadUserSelectedOccupancy(
  action: {
    typeaheadId: string
    occupancy: Array<App.TypeaheadExtractedOccupancy>
  },
): AppAction {
  const { typeaheadId, occupancy } = action

  return {
    type: SET_TYPEAHEAD_USER_SELECTED_OCCUPANCY,
    typeaheadId,
    occupancy,
  }
}

export function streamTypeahead(
  action: {
    input: string
    typeaheadId: string
    listenerId: string
    types?: Array<string>
    limit?: number
    isLuxPlusMember?: boolean
    rollout?: Array<string>
    recentPlaceIds?: Array<string>
  },
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const {
      input,
      typeaheadId,
      listenerId,
      types,
      limit,
      isLuxPlusMember,
      rollout = EmptyArray,
      recentPlaceIds,
    } = action
    const useExactHotelDurations = types === HOTEL_SEARCH_TYPEAHEAD_TYPES

    // Register state listener for cleanup tracking
    addStateListener({ eventGroupKey: typeaheadId, listenerId })

    const currentData = state.search.typeaheadStreamingData[typeaheadId]
    const isListening = currentData?.socketListenersStatus === 'listening'
    const batcher = getOrCreateTypeaheadStreamBatcher(typeaheadId, dispatch)
    batcher.cancelPending()

    // Helper to check if event belongs to current request
    const isCurrentRequest = (responseTypeaheadId: string): boolean => {
      return responseTypeaheadId === typeaheadId
    }

    const handleResults = (response: TypeaheadNaturalLanguageResultsV2Response) => {
      if (isCurrentRequest(response.typeaheadId)) {
        const searchItems = response.results.map(result => mapTypeaheadResultToSearchItemV2(result, useExactHotelDurations))
        const placeIds = Array.from(new Set(
          response.results
            .map((r) => getDisplayPlaceId(r.searchItem.extractedData))
            .filter((id): id is string => Boolean(id)),
        ))
        batcher.enqueue(
          {
            updates: { results: searchItems },
            timestamp: response.requestTimestamp,
            input: response.input,
            extractedDataType: 'results',
          },
          placeIds.length > 0 ? placeIds : undefined,
        )
      }
    }

    const handleEnd = (response: TypeaheadEndResponse) => {
      if (isCurrentRequest(response.typeaheadId)) {
        const searchItem = mapTypeaheadEndEventToSearchItemV2(response, useExactHotelDurations)
        batcher.enqueue({
          updates: { finalSearchItem: searchItem },
          timestamp: response.requestTimestamp,
          input: response.input,
          extractedDataType: 'finalSearchItem',
        })
        batcher.flush()

        clearLastMessage(typeaheadId)
        dispatch(completeTypeaheadStreaming({ typeaheadId: response.typeaheadId, input: response.input }))
        dispatch({
          type: SET_TYPEAHEAD_STREAMING_SOCKET_STATUS,
          typeaheadId,
          status: 'not-listening',
        })
        removeAllSearchSocketListenersAndSafelyDisconnect({ eventGroupKey: typeaheadId })
        removeTypeaheadStreamBatcher(typeaheadId)
      }
    }

    const handleError = (_response: TypeaheadErrorResponse) => {
      batcher.cancelPending()
      clearLastMessage(typeaheadId)
      dispatch(errorTypeaheadStreaming(typeaheadId))
      dispatch({
        type: SET_TYPEAHEAD_STREAMING_SOCKET_STATUS,
        typeaheadId,
        status: 'not-listening',
      })
      removeAllSearchSocketListenersAndSafelyDisconnect({ eventGroupKey: typeaheadId })
      removeTypeaheadStreamBatcher(typeaheadId)
    }

    // Register all socket listeners using searchSocketListeners utility (only once)
    if (!isListening) {
      // Set socket listener status to listening
      dispatch({
        type: SET_TYPEAHEAD_STREAMING_SOCKET_STATUS,
        typeaheadId,
        status: 'listening',
      })

      // Register all socket listeners
      addSearchSocketListener({
        eventGroupKey: typeaheadId,
        event: 'typeahead:response:results:v2',
        handler: handleResults,
      })
      for (const { event, extractedDataType, toUpdates } of TYPEAHEAD_FIELD_SOCKET_EVENTS) {
        addSearchSocketListener({
          eventGroupKey: typeaheadId,
          event,
          handler: (response: unknown) => {
            const typedResponse = response as TypeaheadSocketResponseBase
            if (!isCurrentRequest(typedResponse.typeaheadId)) return
            batcher.enqueue({
              updates: toUpdates(typedResponse, useExactHotelDurations),
              timestamp: typedResponse.requestTimestamp,
              input: typedResponse.input,
              extractedDataType,
            })
          },
        })
      }
      addSearchSocketListener({
        eventGroupKey: typeaheadId,
        event: 'typeahead:response:end',
        handler: handleEnd,
      })
      addSearchSocketListener({
        eventGroupKey: typeaheadId,
        event: 'typeahead:response:error',
        handler: handleError,
      })
    }

    // Always start streaming session for each new request.
    dispatch(startTypeaheadStreaming(typeaheadId))

    const streamingData = state.search.typeaheadStreamingData[typeaheadId]
    const occupancy = streamingData?.userSelectedOccupancy

    // Always emit the request on each call (even if listeners are already set up)
    const extractRequest: TypeaheadExtractRequest = {
      input,
      brand: config.BRAND,
      region: state.geo.currentRegionCode,
      lang: state.geo.locale,
      timezone: getBrowserTimeZone(),
      typeaheadId,
      types,
      limit,
      isLuxPlusMember,
      rollout,
      occupancy,
      recentPlaceIds,
      numberOfSuggestions: 3,
      requestTimestamp: new Date().toISOString(),
    }
    emitSocketMessage(typeaheadId, 'typeahead:request:extract', extractRequest)
  }
}
